import {
  AUTH_COOKIE,
  clearAuthCookies,
  PARTNER_COOKIE,
  REFRESH_COOKIE,
  readCookie,
} from '@artic/ui/lib/auth-cookies';
import { getHubUrl } from '@artic/ui/lib/environment';

/** The hub's Statusboard page — the legacy work orders list. */
export function getHubStatusboardUrl(): string {
  return `${getHubUrl()}/statusboard`;
}

export interface AuthState {
  isAuthenticated: boolean;
  /** JWT access token from the hub's auth cookie. */
  token: string | undefined;
  /**
   * Partner key from the cookie (e.g. "LOW").
   * This is NOT the full partner_name — use `usePartnerName()` to resolve
   * the full name needed for API calls.
   */
  partner: string | undefined;
}

/**
 * Reads auth state from cookies set by the hub.
 * Called once at app startup and passed into the router context.
 *
 * Note: The `partner` value is the short `partner_key` from the cookie.
 * API endpoints expect the full `partner_name`. Components should use
 * `usePartnerName()` from `features/inbox/use-partner-name.ts` to resolve.
 */
export function getAuthState(): AuthState {
  const token = readCookie(AUTH_COOKIE);
  const refreshToken = readCookie(REFRESH_COOKIE);
  const partner = readCookie(PARTNER_COOKIE);

  return {
    // User is potentially authenticated if they have an access token OR a
    // refresh token that can be exchanged for a new access token.
    isAuthenticated: token !== undefined || refreshToken !== undefined,
    token,
    partner,
  };
}

let redirectInFlight = false;

export function redirectToHub(): never {
  const hubUrl = getHubUrl();
  const returnTo = encodeURIComponent(window.location.href);

  // Wipe stale auth cookies before handing off. Reasons:
  // 1. If we're redirecting because refresh failed, those cookies are dead
  //    weight — leaving them set means the hub's own startup `getSession()`
  //    tries to refresh them again against Supabase before our /login mount
  //    handler can clean up, contributing to the auth rate limit
  //    (Sentry ARTIC-HUB-12X).
  // 2. This is also the navbar's logout target, where wiping cookies is the
  //    expected behaviour.
  // Best-effort: cookie-write may fail in private-browsing edge cases — never
  // block the redirect on a cleanup error.
  try {
    const cookieDomain = import.meta.env['VITE_AUTH_COOKIE_DOMAIN'] as string | undefined;
    clearAuthCookies(cookieDomain ? { domain: cookieDomain } : {});
  } catch {
    /* best-effort cookie cleanup */
  }

  // Multiple concurrent 401s can each call `onRefreshFailure`, which in turn
  // calls this function. Browsers ignore subsequent `location.href` writes
  // once a navigation is in flight, but the redundant work still throws from
  // each caller. Latch so only the first call actually navigates.
  if (!redirectInFlight) {
    redirectInFlight = true;
    window.location.href = `${hubUrl}/login?returnTo=${returnTo}`;
  }

  // Never resolves — the browser navigates away
  throw new Error('Redirecting to hub for authentication');
}
